import styled from '@emotion/styled';
import { H3, SmallText, TextLabel } from 'ds/components/typography';
import { Button } from 'ds/components/button';
import { typographyFontSizeCfg, typographyLineHeightCfg } from 'ds/components/typography/types';
import { Link } from 'components/link';
import { SpacingScale } from 'ds/theme/spacing';

export const Root = styled(Link)(({ theme }) => ({
  display: 'flex',
  textDecoration: 'none',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1),
  borderRadius: theme.borderRadius.small,
  backgroundColor: theme.colors.neutrals.white,
  border: `1px solid ${theme.colors.neutrals.grey8}`,
  cursor: 'pointer',
  userSelect: 'none',
}));

export const AgentLogo = styled.div<{ url: string }>(({ theme, url }) => ({
  width: '129px',
  height: '129px',
  borderRadius: theme.borderRadius.round,
  background: `url(${url}) lightgray 50% / cover no-repeat`,
  border: `1px solid ${theme.colors.neutrals.grey6}`,
}));

export const AgentName = styled(H3)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.brand.darker,
  'svg': {
    flexBasis: '21px',
  },
  'span': {
    display: 'block',
    maxWidth: 'calc(170px - 25px)',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
}));

export const OfficeName = styled(SmallText)(({ theme }) => ({
  color: theme.colors.brand.darker,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  width: '100%',
  whiteSpace: 'nowrap',
}));

export const AgentInfo = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  width: '170px',
  'a': {
    display: 'block',
    marginTop: theme.spacing(2),
  },
}));

export const CarouselWrapper = styled.div(({ theme }) => ({
  'div.carousel-shadow-root .view-pager-rail': {
    paddingRight: 0,
  },
  '.view-pager-bow > a:not(:last-of-type)': {
    marginLeft: theme.spacing(1.5),
  },
  [theme.media.createObjectMediaQuery({ to: 2 })]: {
    'div > div.slide-btn': {
      opacity: 1,
      visibility: 'visible',
    },
  },
}));

export const CarouselRoot = styled.div<{ slideIndex: number; withBorder: boolean; disableMargin: boolean; bgColor: string; paddingRight?: SpacingScale}>(({ paddingRight = 3, theme, slideIndex, withBorder, disableMargin, bgColor }) => ({
  width: '100%',
  position: 'relative',
  flexGrow: 1,
  minWidth: '100%',
  ...(disableMargin ? {} : {
    maxWidth: 'calc(100% + 48px)',
    margin: '0 -24px 0',
  }),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(4, paddingRight),
  paddingLeft: 0,
  backgroundColor: bgColor,
  ...(withBorder ? { borderBottom: `1px solid ${theme.colors.neutrals.grey8}` } : {}),
  '.carousel-shadow-root::before': {
    transition: `background 200ms ease`,
  },
  '.carousel-shadow-root::after': {
    ...(slideIndex === 0 ? { background: 'transparent' } : {}),
    transition: `background 200ms ease`,
  },
}));

export const BGIconWrapper = styled.div({
  position: 'absolute',
  left: 0,
  bottom: 0,
});

export const LeadingIconWrapper = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '40px',
  height: '48px',
  overflow: 'hidden',
  'svg': {
    transform: 'translateY(3px)',
  },
});

export const SubmitButton = styled(Button.withComponent('div'))(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: 'unset',
}));

export const Title = styled.div<{ darker: boolean; }>(({ theme, darker }) => ({
  fontWeight: 700,
  color: darker ? theme.colors.neutrals.grey1 : theme.colors.neutrals.white,
  fontSize: `${typographyFontSizeCfg.h2.mobile}px`,
  lineHeight: `${typographyLineHeightCfg.h2.mobile}px`,
}));

export const SubTitle = styled(TextLabel)<{ darker: boolean; }>(({ theme, darker }) => ({
  color: darker ? theme.colors.neutrals.grey4 : theme.colors.neutrals.white,
}));

export const Header = styled.div(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
}));
